import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function shortPubkey(pubkey: string): string {
  return pubkey.slice(0, 8) + '...' + pubkey.slice(-4);
}

export function timeAgo(timestamp: number): string {
  const seconds = Math.floor(Date.now() / 1000 - timestamp);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return new Date(timestamp * 1000).toLocaleDateString();
}

export function formatSats(sats: number): string {
  if (sats >= 1e8) return (sats / 1e8).toFixed(8) + ' BCH';
  if (sats >= 1e6) return (sats / 1e6).toFixed(2) + 'M sats';
  if (sats >= 1e3) return (sats / 1e3).toFixed(1) + 'k sats';
  return sats + ' sats';
}

/**
 * Filter a list of Nostr events to remove originals that have been replaced
 * by an edit (events with a '-edit-of' tag pointing to the original ID).
 *
 * Also preserves the original post's created_at for sorting, so edits don't
 * float to the top of the feed as if they were brand-new posts.
 *
 * Returns the filtered + sorted array with originals hidden and edits placed
 * at the original post's chronological position.
 */
export function deduplicateEdits(events: any[]): any[] {
  const editedOriginalIds = new Set<string>();
  const editOriginalTimestamps = new Map<string, number>(); // editEventId → original created_at

  for (const e of events) {
    const editOfTag = e.tags?.find((t: string[]) => t[0] === '-edit-of');
    if (editOfTag?.[1]) {
      editedOriginalIds.add(editOfTag[1]);
      const original = events.find(o => o.id === editOfTag[1]);
      if (original) {
        editOriginalTimestamps.set(e.id, original.created_at);
      }
    }
  }

  const seen = new Set<string>();
  return events
    .slice()
    .sort((a, b) => {
      const ta = editOriginalTimestamps.get(a.id) ?? a.created_at;
      const tb = editOriginalTimestamps.get(b.id) ?? b.created_at;
      return tb - ta;
    })
    .filter(e => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      // Drop originals that have been replaced by an edit
      if (editedOriginalIds.has(e.id)) return false;
      return true;
    });
}

